/* Shared by the Vue client and the standalone page; no application requests are replayed. */
function createAppUpdateMonitor(options) {
    const schedule = options.schedule || setTimeout;
    const cancel = options.cancel || clearTimeout;
    let active = false;
    let timer = null;
    let pending = null;
    let confirmation = null;
    let revision = 0;
    let value = {status: 'unknown', version: null, updatedAt: null, checking: false,
        required: Boolean(options.required), connectionLost: false, confirming: false,
        navigating: false, navigationError: false};
    const publish = patch => {
        value = Object.assign({}, value, patch);
        options.onChange(Object.assign({}, value));
    };
    const planNext = () => {
        if (timer) cancel(timer);
        if (active) timer = schedule(() => check(), value.required || value.connectionLost ? 5000 : 30000);
    };
    function check() {
        if (pending) return pending;
        if (timer) cancel(timer);
        const requestRevision = revision;
        publish({checking: true});
        pending = Promise.resolve().then(() => options.readStatus()).then(result => {
            if (requestRevision !== revision) return;
            if (!result || !['building', 'ready', 'failed', 'unavailable'].includes(result.status) ||
                (result.status === 'ready' && (typeof result.version !== 'string' || !result.version))) {
                throw new Error('Invalid readiness response');
            }
            const differentVersion = options.clientVersion && options.clientVersion !== 'dev' && result.version !== options.clientVersion;
            publish({status: result.status, version: result.status === 'ready' ? result.version : null,
                updatedAt: result.updatedAt || null,
                required: value.required || result.status !== 'ready' || Boolean(differentVersion),
                connectionLost: false});
        }).catch(() => {
            if (requestRevision === revision) publish({status: 'unknown', version: null});
        }).then(() => {
            pending = null;
            publish({checking: false});
            planNext();
            return value;
        });
        return pending;
    }
    return {
        start() { if (!active) { active = true; return check(); } return pending; },
        stop() { active = false; revision += 1; if (timer) cancel(timer); },
        check,
        notify(status) {
            revision += 1; // In-flight readiness predating an update cannot enable reload.
            publish({required: true, connectionLost: false,
                status: status === 'building' ? 'building' : 'unknown', version: null});
            if (pending) return pending.then(() => check());
            return check();
        },
        reportConnectionLoss() {
            if (value.connectionLost) return pending || Promise.resolve(value);
            revision += 1;
            publish({connectionLost: true, status: 'unknown', version: null});
            if (pending) return pending.then(() => check());
            return check();
        },
        confirm() {
            if (confirmation) return confirmation;
            if (value.navigating) return Promise.resolve(value);
            revision += 1;
            publish({confirming: true, navigationError: false});
            confirmation = (async () => {
                try {
                    if (pending) await pending;
                    const fresh = await check();
                    if (fresh.status === 'ready') {
                        publish({navigating: true, confirming: false});
                        options.reload();
                    }
                    return value;
                } catch (error) {
                    publish({navigating: false, navigationError: true});
                    return value;
                } finally {
                    if (!value.navigating) publish({confirming: false});
                    confirmation = null;
                }
            })();
            return confirmation;
        },
    };
}

// A separate transport intentionally bypasses auth refresh and generic error dialogs.
function readAppUpdateStatus() {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('GET', `/api/app-version?t=${Date.now()}`);
        request.timeout = 10000;
        request.setRequestHeader('Accept', 'application/json');
        request.onload = () => {
            try {
                if (request.status !== 200) throw new Error('Readiness unavailable');
                resolve(JSON.parse(request.responseText));
            } catch (error) { reject(error); }
        };
        request.onerror = request.ontimeout = request.onabort = () => reject(new Error('Readiness connection failed'));
        request.send();
    });
}

if (typeof module !== 'undefined') module.exports = {createAppUpdateMonitor, readAppUpdateStatus};
